import {
  Currency,
  FeeType,
  PaymentMethod,
  PaymentMethodModificationPreviewRequest,
  PaymentMethodModificationRequest,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';
import { InsuranceType } from '@nemuru-eng/js-api-client/slim';

import { AutomobileFormFieldName } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormFieldName';
import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { AutoLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/entities/PaymentMethod/AutoLoanPaymentMethod';
import { EditAutoPaymentMethodFinancingFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { normalizeSpanishLicensePlate } from '@/utils/zodValidation/validateSpanishLicensePlate';

export type PaymentMethodModificationOptions = NonNullable<PaymentMethodModificationRequest['paymentMethodOptions']>;
type PaymentMethodModificationChanges = NonNullable<PaymentMethodModificationRequest['changes']>;
type PaymentMethodModificationChangeKey = keyof PaymentMethodModificationChanges;
type AutoLoanToDtoOptions = ReturnType<AutoLoanPaymentMethod['toDto']>['options'];
type PaymentMethodModificationPreviewChanges = NonNullable<PaymentMethodModificationPreviewRequest['changes']>;
type FinancingAmountChanges = Pick<PaymentMethodModificationChanges, 'principal' | 'downPayment'>;
type FormFieldValue = AutomobileFormItem[AutomobileFormFieldName];
type VehicleCodeFormFieldName = 'brandCode' | 'modelCode' | 'versionCode';
type VehicleCodeChangeKey = Extract<PaymentMethodModificationChangeKey, VehicleCodeFormFieldName>;

interface AppendableVehicleModificationFieldConfig {
  formFieldName: AutomobileFormFieldName;
  changeKey: PaymentMethodModificationChangeKey;
  codeFormFieldName?: VehicleCodeFormFieldName;
  codeChangeKey?: VehicleCodeChangeKey;
  normalize: (value: FormFieldValue) => unknown;
  hasChanged?: (originalValue: unknown, updatedValue: unknown) => boolean;
}

const normalizeString = (value: string | null | undefined): string | undefined => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : undefined;
};

const normalizeNullableString = (value: string | null | undefined): string | null => {
  return normalizeString(value) ?? null;
};

export const toRegistrationDate = (value: Date | string | null | undefined): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const parsedDate = value instanceof Date ? value : new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
};

export const normalizeDate = (value: Date | string | null | undefined): string | undefined => {
  const parsedDate = toRegistrationDate(value);

  if (!parsedDate) {
    return undefined;
  }

  const year = parsedDate.getUTCFullYear();
  const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00+00:00`;
};

const normalizePlateNumber = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalizedValue = normalizeSpanishLicensePlate(value);

  return normalizedValue || null;
};

const normalizeOdometer = (value: string | null | undefined): number | undefined => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return undefined;
  }

  const parsedValue = Number.parseInt(normalizedValue, 10);

  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const normalizeNullableOdometer = (value: string | null | undefined): number | null => {
  return normalizeOdometer(value) ?? null;
};

const normalizePrice = (value: string | null | undefined, currency: Currency) => {
  const normalizedValue = normalizeString(value);

  if (!normalizedValue) {
    return undefined;
  }

  const parsedValue = Number.parseFloat(normalizedValue);

  if (Number.isNaN(parsedValue)) {
    return undefined;
  }

  return { amount: parsedValue, currency };
};

const hasValueChanged = <T>(originalValue: T, updatedValue: T): boolean => originalValue !== updatedValue;

const toNullableInsuranceType = (insurance: InsuranceType | null | undefined): InsuranceType | null => {
  if (!insurance || insurance === InsuranceType.NO) {
    return null;
  }

  return insurance;
};

export const mapAutoLoanToDtoOptionsToModificationOptions = (
  options: AutoLoanToDtoOptions,
  extras?: {
    annualInterest?: number;
    registrationDate?: Date | string | null;
  },
): PaymentMethodModificationOptions => {
  const source = { ...(options ?? {}) } as PaymentMethodModificationOptions;
  const insuranceType = toNullableInsuranceType(source.insuranceType);
  const registrationDate = toRegistrationDate(extras?.registrationDate);

  if (registrationDate) {
    source.registrationDate = registrationDate;
  } else {
    delete source.registrationDate;
  }

  return {
    ...source,
    ...(extras?.annualInterest !== undefined && { annualInterest: extras.annualInterest }),
    hasInsurance: insuranceType !== null,
    hasSetupFee: source.hasSetupFee ?? undefined,
    hasGracePeriod: source.hasGracePeriod ?? undefined,
    hasMerchantDiscountPercentage: source.hasMerchantDiscountPercentage ?? undefined,
  };
};

const VEHICLE_MODIFICATION_FIELD_CONFIGS: AppendableVehicleModificationFieldConfig[] = [
  {
    formFieldName: 'identificationNumber',
    changeKey: 'vin',
    normalize: (value) => normalizeNullableString(value as string | null | undefined),
  },
  {
    formFieldName: 'plateNumber',
    changeKey: 'plateNumber',
    normalize: (value) => normalizePlateNumber(value as string | null | undefined),
  },
  {
    formFieldName: 'odometer',
    changeKey: 'odometer',
    normalize: (value) => normalizeNullableOdometer(value as string | null | undefined),
  },
  {
    formFieldName: 'brand',
    changeKey: 'brand',
    codeFormFieldName: 'brandCode',
    codeChangeKey: 'brandCode',
    normalize: (value) => normalizeString(value as string | null | undefined),
  },
  {
    formFieldName: 'model',
    changeKey: 'model',
    codeFormFieldName: 'modelCode',
    codeChangeKey: 'modelCode',
    normalize: (value) => normalizeString(value as string | null | undefined),
  },
  {
    formFieldName: 'version',
    changeKey: 'version',
    codeFormFieldName: 'versionCode',
    codeChangeKey: 'versionCode',
    normalize: (value) => normalizeString(value as string | null | undefined),
  },
];

const getVehicleModificationFieldConfigs = (currency: Currency): AppendableVehicleModificationFieldConfig[] => [
  ...VEHICLE_MODIFICATION_FIELD_CONFIGS,
  {
    formFieldName: 'price',
    changeKey: 'vehicleAmount',
    normalize: (value) => normalizePrice(value as string | null | undefined, currency),
    hasChanged: (originalValue, updatedValue) =>
      hasValueChanged(
        (originalValue as ReturnType<typeof normalizePrice>)?.amount,
        (updatedValue as ReturnType<typeof normalizePrice>)?.amount,
      ),
  },
];

const setModificationChange = <TChangeKey extends PaymentMethodModificationChangeKey>(
  changes: PaymentMethodModificationChanges,
  changeKey: TChangeKey,
  value: PaymentMethodModificationChanges[TChangeKey],
) => {
  changes[changeKey] = value;
};

const appendVehicleModificationField = (
  config: AppendableVehicleModificationFieldConfig,
  originalItem: AutomobileFormItem,
  updatedItem: AutomobileFormItem,
  changes: PaymentMethodModificationChanges,
) => {
  const originalValue = config.normalize(originalItem[config.formFieldName]);
  const updatedValue = config.normalize(updatedItem[config.formFieldName]);
  const isChanged = config.hasChanged
    ? config.hasChanged(originalValue, updatedValue)
    : hasValueChanged(originalValue, updatedValue);

  if (!isChanged) {
    return;
  }

  if (config.codeFormFieldName && config.codeChangeKey) {
    const nameValue = updatedValue as string | undefined;
    const codeValue = normalizeString(updatedItem[config.codeFormFieldName]);

    if (!nameValue || !codeValue) {
      return;
    }

    setModificationChange(
      changes,
      config.changeKey,
      nameValue as PaymentMethodModificationChanges[typeof config.changeKey],
    );
    setModificationChange(changes, config.codeChangeKey, codeValue);
    return;
  }

  setModificationChange(
    changes,
    config.changeKey,
    updatedValue as PaymentMethodModificationChanges[typeof config.changeKey],
  );
};

export const getAutoLoanPaymentMethodOptions = (
  paymentMethods: PaymentMethod[],
): PaymentMethodModificationOptions | undefined => {
  const autoLoanPaymentMethod = paymentMethods.find(
    (paymentMethod) => paymentMethod.type === PaymentMethodType.AUTO_LOAN,
  );

  if (!autoLoanPaymentMethod || !('options' in autoLoanPaymentMethod)) {
    return undefined;
  }

  return autoLoanPaymentMethod.options as PaymentMethodModificationOptions;
};

const appendFinancingAmountChanges = (
  originalFinancing: EditAutoPaymentMethodFinancingFields,
  updatedFinancing: EditAutoPaymentMethodFinancingFields,
  currency: Currency,
  changes: FinancingAmountChanges,
) => {
  if (
    hasValueChanged(originalFinancing.principalAmount, updatedFinancing.principalAmount) &&
    updatedFinancing.principalAmount !== null
  ) {
    changes.principal = { amount: updatedFinancing.principalAmount, currency };
  }
  if (
    hasValueChanged(originalFinancing.downPaymentAmount, updatedFinancing.downPaymentAmount) &&
    updatedFinancing.downPaymentAmount !== null
  ) {
    changes.downPayment = { amount: updatedFinancing.downPaymentAmount, currency };
  }
};

const buildPaymentMethodOptionsPatch = (
  originalFinancing: EditAutoPaymentMethodFinancingFields,
  updatedFinancing: EditAutoPaymentMethodFinancingFields,
  currentOptions?: PaymentMethodModificationOptions,
): PaymentMethodModificationOptions | undefined => {
  const hasTermChanged =
    hasValueChanged(originalFinancing.term, updatedFinancing.term) && updatedFinancing.term !== null;
  const hasAnnualInterestChanged =
    hasValueChanged(originalFinancing.annualInterest, updatedFinancing.annualInterest) &&
    updatedFinancing.annualInterest !== null;
  const hasInsuranceChanged =
    hasValueChanged(originalFinancing.insurance, updatedFinancing.insurance) ||
    hasValueChanged(originalFinancing.insuranceProvider, updatedFinancing.insuranceProvider);
  const hasSetupFeeChanged =
    hasValueChanged(originalFinancing.setupFeeAmount, updatedFinancing.setupFeeAmount) ||
    hasValueChanged(originalFinancing.setupFeeType, updatedFinancing.setupFeeType);

  if (!hasTermChanged && !hasAnnualInterestChanged && !hasInsuranceChanged && !hasSetupFeeChanged) {
    return undefined;
  }

  const insuranceType = toNullableInsuranceType(updatedFinancing.insurance);
  const derivedHasInsurance = insuranceType !== null;
  const derivedHasSetupFee = (updatedFinancing.setupFeeAmount ?? 0) > 0;

  const paymentMethodOptions: PaymentMethodModificationOptions = {
    ...currentOptions,
    hasInsurance: currentOptions?.hasInsurance ?? derivedHasInsurance,
    hasSetupFee: currentOptions?.hasSetupFee ?? derivedHasSetupFee,
    hasGracePeriod: currentOptions?.hasGracePeriod ?? undefined,
    hasMerchantDiscountPercentage: currentOptions?.hasMerchantDiscountPercentage ?? undefined,
  };

  if (hasTermChanged && updatedFinancing.term !== null) {
    paymentMethodOptions.period = updatedFinancing.term;
  }

  if (hasAnnualInterestChanged && updatedFinancing.annualInterest !== null) {
    paymentMethodOptions.annualInterest = updatedFinancing.annualInterest;
  }

  if (hasInsuranceChanged) {
    paymentMethodOptions.hasInsurance = derivedHasInsurance;

    if (insuranceType) {
      paymentMethodOptions.insuranceType = insuranceType;

      if (updatedFinancing.insuranceProvider) {
        paymentMethodOptions.insuranceProvider = updatedFinancing.insuranceProvider;
      } else {
        delete paymentMethodOptions.insuranceProvider;
      }
    } else {
      delete paymentMethodOptions.insuranceType;
      delete paymentMethodOptions.insuranceProvider;
    }
  }

  if (hasSetupFeeChanged) {
    paymentMethodOptions.hasSetupFee = derivedHasSetupFee;

    if (derivedHasSetupFee && updatedFinancing.setupFeeAmount !== null && updatedFinancing.setupFeeType) {
      paymentMethodOptions.setupFee = {
        paymentType: updatedFinancing.setupFeeType,
        type: FeeType.FIX_AMOUNT,
        value: updatedFinancing.setupFeeAmount,
      };
    } else {
      delete paymentMethodOptions.setupFee;
    }
  }

  return paymentMethodOptions;
};

const getUpdatedRegistrationDate = (
  originalItem: AutomobileFormItem,
  updatedItem: AutomobileFormItem,
  selectedPaymentMethodOptions?: PaymentMethodModificationOptions,
): string | undefined => {
  const originalDate = normalizeDate(originalItem.registrationDate);
  const updatedDate = normalizeDate(selectedPaymentMethodOptions?.registrationDate ?? updatedItem.registrationDate);

  if (!hasValueChanged(originalDate, updatedDate) || !updatedDate) {
    return undefined;
  }

  return updatedDate;
};

const resolvePaymentMethodOptions = (
  originalFinancing?: EditAutoPaymentMethodFinancingFields,
  updatedFinancing?: EditAutoPaymentMethodFinancingFields,
  currentPaymentMethodOptions?: PaymentMethodModificationOptions,
  selectedPaymentMethodOptions?: PaymentMethodModificationOptions,
): PaymentMethodModificationOptions | undefined => {
  if (selectedPaymentMethodOptions) {
    return selectedPaymentMethodOptions;
  }

  if (!originalFinancing || !updatedFinancing) {
    return undefined;
  }

  return buildPaymentMethodOptionsPatch(originalFinancing, updatedFinancing, currentPaymentMethodOptions);
};

export const buildAutoPaymentMethodModificationBody = (
  originalItem: AutomobileFormItem,
  updatedItem: AutomobileFormItem,
  currency: Currency,
  originalFinancing?: EditAutoPaymentMethodFinancingFields,
  updatedFinancing?: EditAutoPaymentMethodFinancingFields,
  currentPaymentMethodOptions?: PaymentMethodModificationOptions,
  selectedPaymentMethodOptions?: PaymentMethodModificationOptions,
): PaymentMethodModificationRequest => {
  const changes: PaymentMethodModificationChanges = {};

  getVehicleModificationFieldConfigs(currency).forEach((config) => {
    appendVehicleModificationField(config, originalItem, updatedItem, changes);
  });

  if (originalFinancing && updatedFinancing) {
    appendFinancingAmountChanges(originalFinancing, updatedFinancing, currency, changes);
  }

  const updatedDate = getUpdatedRegistrationDate(originalItem, updatedItem, selectedPaymentMethodOptions);

  if (updatedDate) {
    changes.registrationDate = updatedDate;
  }

  const paymentMethodOptions = resolvePaymentMethodOptions(
    originalFinancing,
    updatedFinancing,
    currentPaymentMethodOptions,
    selectedPaymentMethodOptions,
  );

  return {
    ...(Object.keys(changes).length > 0 && { changes }),
    ...(paymentMethodOptions && { paymentMethodOptions }),
  };
};

export const toModificationPreviewRequest = (
  body: PaymentMethodModificationRequest,
): PaymentMethodModificationPreviewRequest => {
  const previewChanges: PaymentMethodModificationPreviewChanges = {};
  const { changes } = body;

  if (changes?.odometer !== undefined) {
    previewChanges.odometer = changes.odometer ?? 0;
  }

  if (changes?.registrationDate !== undefined) {
    previewChanges.registrationDate = changes.registrationDate;
  }

  if (changes?.vehicleAmount !== undefined) {
    previewChanges.vehicleAmount = changes.vehicleAmount;
  }

  if (changes?.principal !== undefined) {
    previewChanges.principal = changes.principal;
  }

  if (changes?.downPayment !== undefined) {
    previewChanges.downPayment = changes.downPayment;
  }

  return {
    ...(Object.keys(previewChanges).length > 0 && { changes: previewChanges }),
    ...(body.paymentMethodOptions && { paymentMethodOptions: body.paymentMethodOptions }),
  };
};

export const hasModificationPayload = (body: PaymentMethodModificationRequest): boolean =>
  Boolean(body.changes || body.paymentMethodOptions);
